<template>
    <AppHead title="Willkommen" />
    <div class="grid lg:grid-cols-2">
        <div>
            <PageTitle>Meine Veranstaltungen</PageTitle>
            <div class="mb-4 max-w-xs">
                <input type="search" v-model="params.search" aria-label="Suchen" placeholder="Suchen" class="block w-full rounded-md border-gray-300" />
            </div>
        </div>
        <div class="justify-self-end">
            <a href="https://www.kulturkalender-dresden.de/rubrik-tipp" target="_blank">
                <img src="/img/manager-suggestion-banner.png">
            </a>
        </div>
    </div>
    <ol class="list-decimal text-sm px-4 pb-4">
        <li>
            Wenn Sie einer bestehenden Veranstaltung (gleicher Ort) ein <strong>zusätzliches Datum</strong> hinzufügen möchten,
            nutzen Sie den <strong>"KOPIEREN" Button</strong>.
        </li>
        <li>
            Wenn Sie <strong>mehrere Bilder</strong> zu einer Veranstaltung veröffentlichen möchten,
            nutzen Sie den <strong>"BEARBEITEN" Button</strong> und laden ein zusätzliches Bild hoch.
        </li>
        <li>
            Wenn Sie Ihre Veranstaltung mit einem Rubrik-Tipp <strong>besser platzieren möchten</strong>, nutzen Sie das
            <strong>Euro-Symbol</strong>. Weitere Infos <a href="https://www.kulturkalender-dresden.de/rubrik-tipp" target="_blank">hier</a>.
            (gilt nicht für Ausstellungen und Festivals)
        </li>
    </ol>
    <div class="bg-white rounded-md shadow overflow-x-auto">
        <table class="w-full whitespace-nowrap">
            <thead>
                <tr class="text-left font-bold">
                    <th class="pb-4 pt-6 px-6">
                        <span @click="sort('name')">
                            Titel
                            <ArrowNarrowDownIcon v-if="params.field === 'name' && params.direction === 'asc'" class="inline w-5 h-5 pb-1" />
                            <ArrowNarrowUpIcon v-if="params.field === 'name' && params.direction === 'desc'" class="inline w-5 h-5 pb-1" />
                        </span>
                    </th>
                    <th class="pb-4 pt-6 px-6">Datum</th>
                    <th class="pb-4 pt-6 px-6">
                        <span @click="sort('category')">
                            Rubrik
                            <ArrowNarrowDownIcon v-if="params.field === 'category' && params.direction === 'asc'" class="inline w-5 h-5 pb-1" />
                            <ArrowNarrowUpIcon v-if="params.field === 'category' && params.direction === 'desc'" class="inline w-5 h-5 pb-1" />
                        </span>
                    </th>
                    <th class="pb-4 pt-6 px-6">
                        <span @click="sort('location')">
                            Ort
                            <ArrowNarrowDownIcon v-if="params.field === 'location' && params.direction === 'asc'" class="inline w-5 h-5 pb-1" />
                            <ArrowNarrowUpIcon v-if="params.field === 'location' && params.direction === 'desc'" class="inline w-5 h-5 pb-1" />
                        </span>
                    </th>
                    <th class="pb-4 pt-6 px-6">Aktionen</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="event in events.data" class="hover:bg-gray-100 focus-within:bg-gray-100">
                    <td class="border-t p-4">{{ event.name }}</td>
                    <td class="border-t p-4">
                        {{ event.date }}
                        <span v-if="event.time">{{ event.time }}</span>
                        <span v-if="event.finish">- {{ event.finish }}</span>
                    </td>
                    <td class="border-t p-4">{{ event.category }}</td>
                    <td class="border-t p-4">{{ event.location }}</td>
                    <td class="border-t p-4 inline-flex items-center space-x-4">
                        <a :href="route('event', [event.slug])" target="_blank" class="btn-primary" title="Ansehen">
                            <EyeIcon class="w-5 h-5 text-white" />
                        </a>
                        <Link as="button" :href="route('manager.date.create', [event.slug])" class="btn-primary" title="Kopieren">
                            <DocumentDuplicateIcon class="w-5 h-5 text-white" />
                        </Link>
                        <Link as="button" :href="route('manager.event.edit', [event.slug, event.date_id])" class="btn-primary" title="Bearbeiten">
                            <PencilIcon class="w-5 h-5 text-white" />
                        </Link>
                        <IndexDelete :event="event" />
                        <IndexSuggestion v-if="!(event.date && event.finish)" :event="event" />
                    </td>
                </tr>
                <tr v-if="events.data.length === 0">
                    <td class="px-6 py-4 border-t" colspan="5">Keine Veranstaltungen gefunden.</td>
                </tr>
            </tbody>
        </table>
    </div>
    <div v-if="events.links.length > 3" class="mt-6">
        <div class="flex flex-wrap -mb-1">
            <template v-for="(link, key) in events.links">
                <div v-if="link.url === null" :key="key" class="mb-1 mr-1 px-4 py-3 text-gray-400 text-sm leading-4 border rounded" v-html="link.label" />
                <Link v-else :key="`link-${key}`" class="mb-1 mr-1 px-4 py-3 focus:text-indigo-500 text-sm leading-4 hover:bg-white border focus:border-indigo-500 rounded" :class="{ 'bg-white': link.active }" :href="link.url" v-html="link.label" />
            </template>
        </div>
    </div>
</template>
<script setup>
import {reactive, watch} from "vue";
import {router} from "@inertiajs/vue3";
import {Link} from "@inertiajs/vue3";
import AppHead from '@/Shared/Head';
import PageTitle from '@/Shared/Page/Title';
import IndexDelete from '@/Shared/Index/Delete';
import IndexSuggestion from '@/Shared/Index/Suggestion';
import { EyeIcon, DocumentDuplicateIcon, PencilIcon, ArrowNarrowDownIcon, ArrowNarrowUpIcon } from '@heroicons/vue/outline';
import {pickBy, throttle} from "lodash";

const props = defineProps({
    events: Object,
    filters: Object,
})
const params = reactive({
    search: props.filters.search,
    field: props.filters.field,
    direction: props.filters.direction,
})
watch(params, throttle(() => {
    router.get(route('manager.index'), pickBy(params), {replace: true, preserveState: true});
}, 500))
const sort = (field) => {
    params.direction = (params.field === field && params.direction === 'asc') ? 'desc' : 'asc';
    params.field = field;
}
</script>
